import axios from 'axios'
import { createAxiosClient } from './axiosClient'
import { setupInterceptors } from './setupInterceptors'

export class ApiError extends Error {
  constructor(message, { status, payload } = {}) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.payload = payload
  }
}

export const getApiErrorMessage = (error, fallbackMessage) => {
  const responseMessage =
    error?.payload?.message || error?.payload?.detail || error?.payload?.title
  return typeof responseMessage === 'string' && responseMessage.trim()
    ? responseMessage.trim()
    : fallbackMessage
}

export const createApiClient = ({
  axiosInstance,
  apiKey = '',
  baseUrl = '',
  getSession,
  onUnauthorized,
} = {}) => {
  const client =
    axiosInstance ||
    createAxiosClient({
      baseURL: baseUrl,
    })

  if (axiosInstance && !client.defaults.baseURL) {
    client.defaults.baseURL = baseUrl
  }

  setupInterceptors(client, { apiKey, getSession, onUnauthorized })

  const addRequestInterceptor = (interceptor) => {
    const interceptorId = client.interceptors.request.use(interceptor)
    return () => client.interceptors.request.eject(interceptorId)
  }

  const request = async (path, options = {}) => {
    const { body, headers: requestHeaders = {}, ...requestOptions } = options

    try {
      const response = await client.request({
        ...requestOptions,
        url: path,
        data: body,
        headers: requestHeaders,
      })
      return response.data
    } catch (error) {
      if (!axios.isAxiosError(error)) throw error

      throw new ApiError(
        error.response
          ? `Request failed with status ${error.response.status}`
          : error.message,
        {
          status: error.response?.status,
          payload: error.response?.data,
        },
      )
    }
  }

  return {
    addRequestInterceptor,
    delete: (path, options) => request(path, { ...options, method: 'DELETE' }),
    get: (path, options) => request(path, { ...options, method: 'GET' }),
    patch: (path, options) => request(path, { ...options, method: 'PATCH' }),
    post: (path, options) => request(path, { ...options, method: 'POST' }),
    put: (path, options) => request(path, { ...options, method: 'PUT' }),
    request,
  }
}
